import { HttpResponse } from "@angular/common/http";

import { CreditDebitIndication } from "@core/enums/credit-debit-indication";
import { MatchType } from "@core/enums/match-type";
import { BankBatch } from "@core/models/bank-batch-model";
import { BankCollectionBatch } from "@core/models/bank-collection-batch";
import { Match } from "@core/models/match.model";
import { PendingTransaction } from "@core/models/pending-transaction.model";
import { Transaction } from "@core/models/transaction.model";

export class TransactionHelper {
  static isUnmatchedTransaction(transaction: Transaction): boolean {
    if (!transaction.suppressed) {
      const unmatched: Array<Match> = transaction.matches.filter(
        (match: Match): boolean => match.matchType > MatchType.Manual
      );
      if (
        unmatched.length &&
        transaction.matches.length - unmatched.length === 0
      ) {
        return true;
      }
    }
    return false;
  }

  static getTransactionList(
    selectedBatchId: string | undefined,
    bankBatchList: Array<BankBatch>
  ): Array<Transaction> {
    let transactionList: Array<Transaction> = [];
    if (selectedBatchId === "LastFiveBatches") {
      for (const bankBatch of bankBatchList) {
        transactionList = [
          ...transactionList,
          ...bankBatch.batch!.transactions,
        ];
      }
      return transactionList;
    }
    transactionList = bankBatchList.find(
      (bankBatch: BankBatch): boolean => bankBatch.batch?.id === selectedBatchId
    )!.batch!.transactions;
    return transactionList;
  }

  static getGfhAccountIds(transaction: Transaction): Array<string> {
    const idList: Array<string> = [];
    transaction.matches.forEach((match: Match): void => {
      if (match.gfhAccountId) {
        idList.push(match.gfhAccountId);
      }
    });
    return idList;
  }

  static getBlobInformation(file: HttpResponse<ArrayBuffer>): {
    data: Blob;
    fileName: string;
  } {
    const fileName = file.headers
      .get("Content-Disposition")
      ?.split(";")[1]
      .trim()
      .split("=")[1]
      .replace(/"/g, "")!;
    const data = new Uint8Array(file.body!);
    return { data: new Blob([data], { type: "text/xml" }), fileName };
  }

  static getMaxDateTimestamp(
    collectionBatchList: Array<BankCollectionBatch>
  ): number {
    const allDates = collectionBatchList
      .filter(
        (collection: BankCollectionBatch): boolean =>
          !collection.collectionBatch?.definitive
      )
      .map(
        (collection: BankCollectionBatch): Date =>
          collection.collectionBatch?.utcInsertDate!
      );
    return Math.max(
      ...allDates.map((date: Date): number => new Date(date).getTime())
    );
  }

  static getRelativeAmount(
    amount: number,
    creditDebitIndication: CreditDebitIndication
  ): number {
    return creditDebitIndication === CreditDebitIndication.Debit && amount > 0
      ? amount * -1
      : amount;
  }

  static getUnselectedDisbursementList(
    disbursementList: Array<PendingTransaction>,
    selectedDisbursementList: Array<PendingTransaction>
  ): Array<PendingTransaction> {
    return disbursementList.filter(
      (disbursement: PendingTransaction): boolean =>
        !selectedDisbursementList.includes(disbursement)
    );
  }
}
